import type { ReactNode } from "react";
import type { JSONContent } from "@tiptap/core";

/**
 * Text held as what it means rather than as one string. A run is a stretch of the writer's words, and `reference` names the thing they
 * stand for where they stand for one. What it *shows* is never held here: a
 * numeral is projected from the registry's order, and storing one would freeze a
 * number the drawings can move. `textOf` joins the words for every other reader.
 */
export interface TextRun {
  reference: string | null;
  /** What the words state, as the consumer names it: carried on them through every edit, drawn nowhere. */
  stated?: string;
  text: string;
}

/** One block of text: its runs, how deep it sits, and whether it is numbered. */
export interface TextBlock {
  runs: TextRun[];
  depth: number;
  numbered: boolean;
}

/** The words of a block, which is what a reader that does not care about references sees. */
export function textOf(block: { runs: readonly { text: string }[] }): string {
  return block.runs.map((run) => run.text).join("");
}

/** A document always holds a block, so there is always a line to put a caret in. */
const EMPTY_BLOCK: TextBlock = { runs: [], depth: 0, numbered: false };

/** The blocks as a document: one paragraph each, carrying its own depth and numbering. */
export function asDocument(blocks: readonly TextBlock[]): JSONContent {
  // The schema says a document holds at least one block, and one holding none
  // comes up with no line to click into, so every consumer would have to invent
  // a first line of its own.
  const held = blocks.length === 0 ? [EMPTY_BLOCK] : blocks;
  return {
    type: "doc",
    content: held.map((block) => ({
      type: "paragraph",
      attrs: { depth: block.depth, numbered: block.numbered },
      content: block.runs
        .filter((run) => run.text !== "")
        .map((run) => ({
          ...(run.reference === null
            ? { type: "text", text: run.text }
            : {
                type: "reference",
                attrs: { reference: run.reference, text: run.text },
              }),
          ...(run.stated === undefined
            ? {}
            : { marks: [{ type: STATED, attrs: { stated: run.stated } }] }),
        })),
    })),
  };
}

/** The mark the editor carries what words state in, named once for both directions. */
export const STATED = "stated";

/** What one node's words state, or nothing where they state nothing. */
function statedOf(node: JSONContent): { stated?: string } {
  const mark = node.marks?.find((held) => held.type === STATED);
  const stated: unknown = mark?.attrs?.stated;
  return typeof stated === "string" ? { stated } : {};
}

/** The words of one node, what they name where they name anything, and what they state. */
function runOf(node: JSONContent): TextRun | null {
  if (node.type === "text" && node.text !== undefined)
    return { reference: null, ...statedOf(node), text: node.text };
  if (node.type !== "reference") return null;
  const named: unknown = node.attrs?.reference;
  const words: unknown = node.attrs?.text;
  if (typeof named !== "string" || typeof words !== "string") return null;
  return { reference: named, ...statedOf(node), text: words };
}

/**
 * The blocks a document now holds. Neighbouring text naming one thing is one run: the editor splits on
 * differences a reader of the runs is not asking about, and two runs naming one
 * element would read as two references to it.
 */
export function asBlocks(document: JSONContent): TextBlock[] {
  return (document.content ?? []).map((paragraph) => {
    const runs: TextRun[] = [];
    for (const node of paragraph.content ?? []) {
      const run = runOf(node);
      if (run === null) continue;
      const last = runs.at(-1);
      // Plain words stating one thing are one run; two references are two,
      // even where they name the same thing, since each is its own object.
      if (
        last !== undefined &&
        last.reference === null &&
        run.reference === null &&
        last.stated === run.stated
      )
        last.text += run.text;
      else runs.push(run);
    }
    const attrs = paragraph.attrs ?? {};
    return {
      runs,
      depth: typeof attrs.depth === "number" ? attrs.depth : 0,
      numbered: attrs.numbered === true,
    };
  });
}

/** Words ending at the caret that a name could still be matched against. */
export interface TypedPhrase {
  /** Where the words start, as an offset in the block's text. */
  from: number;
  text: string;
}

/** A name is at most this many words, so the search never walks the whole block. */
const LONGEST_NAME_WORDS = 4;

/**
 * Every phrase ending at the caret that a name might match, longest first. A
 * thing is often named in two or three words ("hilt body"), so the last word
 * alone would never offer them, and the longest is the better answer where both
 * match. A phrase never crosses punctuation: words either side of a semicolon
 * are not one name.
 */
export function phrasesBefore(text: string, caret: number): TypedPhrase[] {
  const before = text.slice(0, caret);
  const started = /[^\s;:,.()]*$/.exec(before);
  if (started === null || started[0] === "") return [];
  const phrases: TypedPhrase[] = [];
  let at = started.index;
  for (let words = 1; words <= LONGEST_NAME_WORDS; words += 1) {
    phrases.push({ from: at, text: before.slice(at) });
    const earlier = /[^\s;:,.()]+\s+$/.exec(before.slice(0, at));
    if (earlier === null) break;
    at = earlier.index;
  }
  return phrases.reverse();
}

/**
 * Where a list of the given width opens under the caret, inside what holds it.
 * The list follows the words, but it is wider than a narrow column, so opening
 * it at the caret there would run it off the screen. It slides back to the right
 * edge instead, and never past the left one.
 */
export function offerLeft(
  caretLeft: number,
  boxLeft: number,
  boxWidth: number,
  listWidth: number,
): number {
  return Math.max(0, Math.min(caretLeft - boxLeft, boxWidth - listWidth));
}

/**
 * The words a chosen name puts in place of the words typed so far.
 *
 * Choosing completes the noun, so the claim and the drawing call the part the
 * same thing: a claim set whose terms drift is one an examiner reads as two
 * parts. The typed capital is kept, since the registry holds a name lower case
 * and a limitation that opens a block is capitalised.
 */
export function completed(typed: string, name: string): string {
  const opened = typed.slice(0, 1);
  if (opened === "" || opened !== opened.toUpperCase()) return name;
  return name.slice(0, 1).toUpperCase() + name.slice(1);
}

/** One thing the writer could mean by the words they are typing. */
export interface ReferenceChoice {
  reference: string;
  label: string;
  /** What the thing is numbered, drawn before its label; none for a thing not numbered yet. */
  numeral?: string;
  detail?: string;
}

/**
 * Where the words at the caret are matched, and what a match shows. The source
 * is the consumer's, so the same editor serves a claim naming a drawn
 * element and anything else that names things from a list of its own. Nothing in
 * the editor knows what a figure is.
 */
export interface ReferenceSource {
  /** What the writer might mean by these words, best first. An empty list offers nothing. */
  suggest: (phrase: string) => readonly ReferenceChoice[];
  tagOf: (reference: string) => string | null;
  /** What a tag says beyond itself, for a source whose tags have more to say. */
  markOf?: (reference: string) => ReferenceMark | null;
}

/** What a named stretch's tag says on hover, whether it is borne out yet, and where a press on it goes. */
export interface ReferenceMark {
  title: string;
  /** The tag stands for something not yet borne out, and is drawn as provisional. */
  unconfirmed: boolean;
  /** Where a press on the tag goes; a tag with nowhere to go has none. */
  open?: () => void;
}

/** The words matched, what they could name, and where under them to draw the list. */
export interface Offer {
  from: number;
  to: number;
  text: string;
  choices: readonly ReferenceChoice[];
  at: { left: number; top: number };
}

/**
 * One act on the block the caret sits in, offered on the editor's own bar. It
 * changes how the block is set, never its words: indenting or numbering says
 * what the block is, and the prose is the writer's. `run` is a plain function of
 * the block, so a consumer writes no editor code.
 */
export interface BlockControl {
  id: string;
  /** Acts that belong together, drawn as one run with a rule after them. */
  group?: string;
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  active?: (block: TextBlock) => boolean;
  enabled?: (block: TextBlock) => boolean;
  run: (block: TextBlock) => { depth: number; numbered: boolean };
}

/** What the editor does beyond holding words. Each is off until a consumer asks for it. */
export interface StructuredTextFeatures {
  /**
   * Enter starts a block and Tab moves one, no deeper than ``deepest``.
   *
   * ``under`` says how deep a started block sits: a claim's preamble is followed
   * by elements, never by a second preamble. Left out, it follows its neighbour.
   */
  nesting?: { deepest: number; under?: (block: TextBlock) => number };
  /** Words can name something, offered as they are typed. */
  references?: ReferenceSource;
  /** How each block is set, so the consumer draws its own depth and type. */
  blockClass?: (block: TextBlock) => string;
  /**
   * What each block is marked with, read over the whole set at once because a
   * marker counts the blocks before it. Null where a block carries none.
   */
  blockMarkers?: (blocks: readonly TextBlock[]) => readonly (string | null)[];
  /**
   * The most blocks the words may hold, and what to say when one more is asked
   * for. Refused here rather than on the way out, which would leave the writer
   * looking at a line nothing keeps.
   */
  capacity?: { blocks: number; refused: () => void };
  /**
   * A value that changes when what the tags and markers are drawn from has
   * moved, so they are drawn again although the words have not. The consumer
   * says so: a function's identity answers the wrong question both ways, being
   * new on every render when written inline and unchanged when its source moves.
   */
  redrawOn?: unknown;
  /**
   * What the empty field says to type, in muted words where the words will be. A field drawn
   * as the words it holds draws nothing while it holds none, so without this an empty field
   * is not seen at all.
   */
  placeholder?: string;
  /**
   * The acts on the editor's bar, which is drawn while the writer is in the
   * words. A page of fields would otherwise carry a wall of bars nobody is using.
   */
  controls?: {
    acts: readonly BlockControl[];
    /** A word to the writer, drawn at the end of the bar: the keys, or a refusal. */
    note?: ReactNode;
  };
}
